"use client";

import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import { Fieldset } from "@tillix/ui-atoms/fieldset";
import { Input } from "@tillix/ui-atoms/input";
import { Radio, RadioGroup } from "@tillix/ui-atoms/radio-group";
import { Controller, useFormContext } from "react-hook-form";
import type { NigoFormValues } from "../types";

const isNumberRegex = /^\d+$/;

export default function AchSection() {
  const { control } = useFormContext<NigoFormValues>();

  return (
    <Fieldset className="max-w-full">
      <Fieldset.Legend>Bank Account Information</Fieldset.Legend>

      <div className="flex gap-4">
        <Controller
          control={control}
          name="achFirstName"
          render={({ field, fieldState }) => (
            <FieldRoot
              className="flex-1"
              invalid={!!fieldState.error}
              name={field.name}
            >
              <FieldLabel>First Name</FieldLabel>
              <FieldControl
                render={(defaultProps) => (
                  <Input
                    {...defaultProps}
                    {...field}
                    autoComplete="off"
                    className={defaultProps.className ?? ""}
                    placeholder="First Name"
                    style={defaultProps.style ?? {}}
                  />
                )}
              />
              {fieldState.error && (
                <FieldError match={true}>{fieldState.error.message}</FieldError>
              )}
            </FieldRoot>
          )}
          rules={{ required: "First name is required" }}
        />

        <Controller
          control={control}
          name="achLastName"
          render={({ field, fieldState }) => (
            <FieldRoot
              className="flex-1"
              invalid={!!fieldState.error}
              name={field.name}
            >
              <FieldLabel>Last Name</FieldLabel>
              <FieldControl
                render={(defaultProps) => (
                  <Input
                    {...defaultProps}
                    {...field}
                    autoComplete="off"
                    className={defaultProps.className ?? ""}
                    placeholder="Last Name"
                    style={defaultProps.style ?? {}}
                  />
                )}
              />
              {fieldState.error && (
                <FieldError match={true}>{fieldState.error.message}</FieldError>
              )}
            </FieldRoot>
          )}
          rules={{ required: "Last name is required" }}
        />
      </div>

      <Controller
        control={control}
        name="routingNumber"
        render={({ field, fieldState }) => (
          <FieldRoot invalid={!!fieldState.error} name={field.name}>
            <FieldLabel>Routing Number</FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  {...field}
                  autoComplete="off"
                  className={defaultProps.className ?? ""}
                  inputMode="numeric"
                  maxLength={9}
                  onChange={(e) =>
                    field.onChange(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="9-digit routing number"
                  style={defaultProps.style ?? {}}
                />
              )}
            />
            {fieldState.error && (
              <FieldError match={true}>{fieldState.error.message}</FieldError>
            )}
          </FieldRoot>
        )}
        rules={{
          required: "Routing number is required",
          pattern: {
            value: /^\d{9}$/,
            message: "Routing number must be 9 digits",
          },
        }}
      />

      <Controller
        control={control}
        name="accountNumber"
        render={({ field, fieldState }) => (
          <FieldRoot invalid={!!fieldState.error} name={field.name}>
            <FieldLabel>Account Number</FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  {...field}
                  autoComplete="off"
                  className={defaultProps.className ?? ""}
                  inputMode="numeric"
                  maxLength={17}
                  onChange={(e) =>
                    field.onChange(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="Account number"
                  style={defaultProps.style ?? {}}
                />
              )}
            />
            {fieldState.error && (
              <FieldError match={true}>{fieldState.error.message}</FieldError>
            )}
          </FieldRoot>
        )}
        rules={{
          required: "Account number is required",
          minLength: {
            value: 4,
            message: "Account number must be at least 4 digits",
          },
          maxLength: {
            value: 17,
            message: "Account number must be at most 17 digits",
          },
          pattern: {
            value: isNumberRegex,
            message: "Account number must contain only digits",
          },
        }}
      />

      <Controller
        control={control}
        name="accountType"
        render={({ field, fieldState }) => (
          <FieldRoot invalid={!!fieldState.error} name={field.name}>
            <FieldLabel>Account Type</FieldLabel>
            <RadioGroup
              className="flex gap-4"
              onValueChange={(val) => field.onChange(val)}
              value={field.value}
            >
              {/** biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
              <label className="flex items-center gap-2 text-sm">
                <Radio value="checking" />
                Checking
              </label>
              {/** biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
              <label className="flex items-center gap-2 text-sm">
                <Radio value="savings" />
                Savings
              </label>
            </RadioGroup>
            {fieldState.error && (
              <FieldError match={true}>{fieldState.error.message}</FieldError>
            )}
          </FieldRoot>
        )}
        rules={{ required: "Account type is required" }}
      />
    </Fieldset>
  );
}
